import React from "react";
import { Table } from "react-bootstrap";
import AnalyticsStatHero from "./AnalyticsStatHero";
import MonthlyBankCashHero from "./MonthlyBankCashHero";
import { formatInr, formatInrDecimal } from "../../utils/financeFormat";
import TableTotalAmount, { ANALYTICS_TABLE_TOTAL_ROW_CLASS } from "./TableTotalAmount";

function periodKeyShortLabel(pk) {
  if (!pk || !/^\d{4}-\d{2}$/.test(pk)) return pk || "—";
  const [y, m] = pk.split("-").map(Number);
  try {
    return new Intl.DateTimeFormat("en-IN", { month: "short", year: "2-digit" }).format(new Date(y, m - 1, 1));
  } catch {
    return pk;
  }
}

export function MonthlyMetricTrend({ metricLabel, valueFn, comparison }) {
  const months = comparison?.months || [];
  if (months.length < 2) return null;
  const trailing = comparison?.trailingMonths || months.length;
  return (
    <div className="mb-4 finance-recon-scroll">
      <h4 className="h6 text-info mb-2">Business trend — last {trailing} months</h4>
      <Table
        striped
        bordered
        hover
        size="sm"
        variant="dark"
        responsive
        className="mb-0 finance-comparison-table"
      >
        <thead>
          <tr>
            <th>Metric</th>
            {months.map((m) => (
              <th key={`trend-h-${metricLabel}-${m.periodKey}`} className="text-end">
                {periodKeyShortLabel(m.periodKey)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{metricLabel}</td>
            {months.map((m) => (
              <td key={`trend-${metricLabel}-${m.periodKey}`} className="text-end">
                {formatInr(valueFn(m))}
              </td>
            ))}
          </tr>
        </tbody>
      </Table>
    </div>
  );
}

/** Consistent monthly-cost tab: trend + hero total + line items (dark tables). */
export default function MonthlyCostTabShell({
  comparison,
  trendLabel,
  valueFn,
  totalLabel,
  totalRupee,
  periodMonth,
  heroMeta,
  lineHint,
  lines = [],
  amountField = "debitRupee",
  emptyMessage = "No transactions this month.",
  amountTone = "expense",
  cashRupee = 0,
  /** When true, show Hyper Gmail Price unit + GST amt columns on line items. */
  hyperPricingColumns = false,
  children,
}) {
  const isCredit = amountField === "creditRupee" || amountTone === "credit";
  const bankNum = Number(totalRupee) || 0;
  const cashNum = Number(cashRupee) || 0;
  const totalNum = bankNum + cashNum;
  const lineTotal = lines.reduce((s, r) => s + (Number(r[amountField]) || 0), 0);
  const hyperGstTotal = hyperPricingColumns
    ? lines.reduce((s, r) => s + (Number(r.gstAmountRupee) || 0), 0)
    : 0;
  const heroLabel = periodMonth ? `${totalLabel} — ${periodMonth}` : totalLabel;

  const defaultMeta = (
    <>
      {lines.length > 0 ? (
        <div>
          Line items: <strong className="text-light">{lines.length}</strong>
        </div>
      ) : null}
      {lines.length > 0 && lineTotal > 0 && lineTotal !== totalNum ? (
        <div className="text-white-50">Lines sum: {formatInr(lineTotal)}</div>
      ) : null}
    </>
  );

  return (
    <>
      <MonthlyMetricTrend metricLabel={trendLabel} valueFn={valueFn} comparison={comparison} />
      {cashNum > 0 || heroMeta ? (
        <MonthlyBankCashHero
          label={totalLabel}
          periodMonth={periodMonth}
          bankRupee={bankNum}
          cashRupee={cashNum}
          valueClassName={isCredit ? "text-info" : "text-warning"}
          extraMeta={heroMeta ?? (lines.length ? defaultMeta : null)}
        />
      ) : (
        <AnalyticsStatHero
          label={heroLabel}
          amountRupee={bankNum}
          meta={heroMeta ?? (lines.length ? defaultMeta : null)}
          valueClassName={isCredit ? "text-info" : ""}
        />
      )}
      {lineHint ? <p className="text-secondary small mb-2">{lineHint}</p> : null}
      <div className="finance-recon-scroll">
        <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Bank</th>
              <th>Category</th>
              <th>Particulars</th>
              {hyperPricingColumns ? (
                <>
                  <th className="text-end">Qty</th>
                  <th className="text-end">Price unit</th>
                  <th className="text-end">GST amt</th>
                </>
              ) : null}
              <th className="text-end">Amount ₹</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={hyperPricingColumns ? 8 : 5} className="text-secondary small">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              <>
                {lines.map((r, idx) => (
                  <tr key={`line-${r.txnDateIso}-${idx}`}>
                    <td>{r.txnDateIso}</td>
                    <td>{r.bank}</td>
                    <td>{r.category || "—"}</td>
                    <td className="small">{r.particulars}</td>
                    {hyperPricingColumns ? (
                      <>
                        <td className="text-end">
                          {r.qtyTotal != null
                            ? `${r.qtyTotal}${r.qtyUnit ? ` ${r.qtyUnit}` : ""}`
                            : "—"}
                        </td>
                        <td className="text-end">{formatInrDecimal(r.unitPriceRupee)}</td>
                        <td className="text-end">{formatInrDecimal(r.gstAmountRupee)}</td>
                      </>
                    ) : null}
                    <td
                      className={`text-end fw-semibold ${isCredit ? "text-success" : "text-warning"}`}
                    >
                      {formatInr(r[amountField])}
                    </td>
                  </tr>
                ))}
                <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                  <td colSpan={hyperPricingColumns ? 6 : 4}>
                    Total ({lines.length} {lines.length === 1 ? "item" : "items"})
                  </td>
                  {hyperPricingColumns ? (
                    <td className="text-end fs-6">
                      <TableTotalAmount amount={hyperGstTotal} toneClass="text-secondary" />
                    </td>
                  ) : null}
                  <td className="text-end fs-6">
                    <TableTotalAmount
                      amount={totalNum}
                      toneClass={isCredit ? "text-success" : "text-warning"}
                    />
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </Table>
      </div>
      {children}
    </>
  );
}
